import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import moment from 'moment';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  provideNativeDateAdapter
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin, map, Observable, of, ReplaySubject, startWith, Subject, take, takeUntil } from 'rxjs';
import { InputMaskDirective } from './input-mask.directive';
import { ModalDialogComponent, SvgIconComponent } from '@k12/ui-primitives';
import { SvgIcon, ToastMessageType, EmployeeDataModel, SubService, ModalType, SeverityLevel, State, EmployeeDetailService, EmployeeSchoolOption, EmployeeSubmitPayload, ProviderType, Address } from '@k12/models';
import { AppInsightsService, CountiesService, CountyResponse, LoadingSpinnerService, ProviderService, ToastMessageService } from '@k12/app-services';
import { FormInlineErrorComponent } from '@k12/forms';
import {
  minYearValidator,
  nameValidator,
  noWhitespaceValidator,
  tenDigitNumberValidator,
  pastDateValidator,
  requiredEmailValidator,
  ssnLast4Validator,
  zipCodeValidator
} from '@k12/validators';
import { ConsentFormComponent, ConsentFormResult } from './consent-form/consent-form.component';

const MIN_REALISTIC_YEAR = 1900;
const MIN_REALISTIC_DATE = moment(`${MIN_REALISTIC_YEAR}-01-01`, 'YYYY-MM-DD');

/** Temporary until API model adds email/NPI fields. */
type EmployeeDetailFormData = EmployeeDataModel & {
  email?: string | null;
  npiNumber?: string | null;
  npiNumber2?: string | null;
};

type EmployeeOccupationType = ProviderType.Tutor | ProviderType.Therapy | ProviderType.Transportation;

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY'
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

const ADD_NEW_EMPLOYEE_LABEL = (occupation: string) => `Add New ${occupation}`;
const EDIT_EMPLOYEE_LABEL = (occupation: string) => `Edit ${occupation}`;
const SELECT_EMPLOYEE_TYPE_LABEL = 'Select Employee Type';
const EMPLOYEES_BREADCRUMB_LABEL = 'Employees';
const EMPLOYEES_LIST_ROUTE = '/employee';
const FAILED_TO_LOAD_DATA = 'Failed to load data.';
const FAILED_TO_LOAD_DATA_TOAST = 'Failed to load data. Please try again later.';
const COUNTY_REQUIRED_STATE_CODE = 'NC';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-shared-employee-detail',
  imports: [
    AsyncPipe,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDialogModule,
    SvgIconComponent,
    ReactiveFormsModule,
    InputMaskDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatIconModule,
    RouterModule,
    FormInlineErrorComponent
  ],
  templateUrl: './employee-detail.component.html',
  styleUrl: './employee-detail.component.scss',
  providers: [
    provideNativeDateAdapter(),
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    DatePipe
  ]
})
export class SharedEmployeeDetailComponent implements OnInit, OnDestroy {
  @Input() set viewOnly(value: boolean) {
    this._viewOnly = value;
    this.updateFormDisabledState();
  }

  @Input() employeeService!: EmployeeDetailService;
  @Input() providerId!: string;
  @Output() submitForm = new EventEmitter();
  @Output() typeSelected = new EventEmitter();

  public displaySchool = (school: EmployeeSchoolOption | string | null): string => {
    if (!school || typeof school === 'string') {
      return (school as string) ?? '';
    }
    return `${school.schoolCode} - ${school.schoolName}`;
  };

  public displayState = (state: State | string | null): string => {
    if (!state || typeof state === 'string') {
      return (state as string) ?? '';
    }
    return state.name;
  };

  get getHeaderLabel(): string {
    const employeeType = this.employeeData().employeeType;
    if (this.selectedId) {
      return EDIT_EMPLOYEE_LABEL(this.getOccupation(employeeType));
    }

    if (!employeeType) {
      return SELECT_EMPLOYEE_TYPE_LABEL;
    }

    return ADD_NEW_EMPLOYEE_LABEL(this.getOccupation(employeeType));
  }

  get details(): FormArray {
    return this.employeeForm.get('details') as FormArray;
  }

  get licensings(): FormArray {
    return this.employeeForm.get('licensing') as FormArray;
  }
  get schoolCodes(): FormArray {
    return this.employeeForm.get('schoolCode') as FormArray;
  }

  get subservices(): FormArray {
    return this.employeeForm.get('subservice') as FormArray;
  }

  public get viewOnly(): boolean {
    return this._viewOnly;
  }

  public readonly tutorSVG = SvgIcon.Tutor;
  public readonly therapySVG = SvgIcon.Therapy;
  public readonly transportationSVG = SvgIcon.Transportation;
  public readonly minRealisticDate = MIN_REALISTIC_DATE;

  public breadcrumbItems = signal<{ label: string; route?: string; action?: () => void }[]>([]);
  readonly chevronRightIcon = SvgIcon.ChevronRight;
  public consentFormResult = signal<ConsentFormResult | null>(null);

  public employeeData: WritableSignal<EmployeeDataModel> = signal<EmployeeDataModel>({} as EmployeeDataModel);
  public employeeForm!: FormGroup;
  public employees: EmployeeDataModel[] | null = null;
  public employeeIndex!: number;

  counties: CountyResponse[] = [];
  filteredCounties: Observable<CountyResponse[]> = of([]);
  public filteredSchools = new ReplaySubject<EmployeeSchoolOption[]>(1);
  public filteredStates = new ReplaySubject<State[]>(1);

  isNorthCarolina = signal(false);
  public loading = signal(false);

  public schools: EmployeeSchoolOption[] = [];
  public selectedId: string | null = null;
  public selectedSubservice!: string;
  public selectingType = signal(true);
  public states: State[] = [];
  public subServices = signal<SubService[]>([]);

  public tutorType: EmployeeOccupationType = ProviderType.Tutor;
  public therapyType: EmployeeOccupationType = ProviderType.Therapy;
  public transportationType: EmployeeOccupationType = ProviderType.Transportation;

  public readonly cityErrorMessages: Record<string, string | ((value: unknown) => string)> = {
    required: 'This field is required',
    name: 'City must contain only letter and special characters'
  };

  protected onDestroy = new Subject<void>();

  private readonly countiesService = inject(CountiesService);
  private formCreated: boolean = false;
  private editableFieldsAllowlist: Set<string> | null = null;
  private readonly route = inject(ActivatedRoute);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private _viewOnly: boolean = false;
  private readonly providerService = inject(ProviderService);
  private readonly toastService = inject(ToastMessageService);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly dialogService = inject(MatDialog);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly cdr = inject(ChangeDetectorRef);

  private dialogRef!: MatDialogRef<ModalDialogComponent>;

  constructor() {
    this.resetEmployeeForm();
  }

  public ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe((params: ParamMap) => {
      this.parseEditableFieldsAllowlist(params.get('editableFields'));
      if (this.formCreated) {
        this.updateFormDisabledState();
      }
    });

    this.route.paramMap.pipe(take(1), takeUntil(this.onDestroy)).subscribe((params: ParamMap) => {
      const employeeIdTmp = params.get('employeeId') ? params.get('employeeId') : null;
      let tmp = params.get('id') ? params.get('id') : null;

      if (employeeIdTmp) {
        tmp = employeeIdTmp;
      }

      this.loading.set(true);
      if (tmp == 'new') {
        this.breadcrumbItems.set([
          { label: EMPLOYEES_BREADCRUMB_LABEL, route: EMPLOYEES_LIST_ROUTE },
          { label: ADD_NEW_EMPLOYEE_LABEL(this.getOccupation()) }
        ]);
        this.getDataForNew();
      } else if (tmp) {
        this.selectedId = tmp;
        this.getData(this.selectedId);
      } else {
        this.loading.set(false);
      }
    });
  }

  public ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public onSelectType(type: EmployeeOccupationType): void {
    this.loading.set(true);
    this.formCreated = false;
    this.resetEmployeeForm();
    this.breadcrumbItems.set([
      { label: EMPLOYEES_BREADCRUMB_LABEL, route: EMPLOYEES_LIST_ROUTE },
      { label: ADD_NEW_EMPLOYEE_LABEL(this.getOccupation()), action: () => this.onNewEmployeeClick() },
      { label: ADD_NEW_EMPLOYEE_LABEL(this.getOccupation(type)) }
    ]);
    this.selectingType.set(false);
    if (this.employeeData() && this.selectedId) {
      this.getData(this.selectedId);
    } else {
      this.employeeData.set({
        employeeType: type,
        id: null,
        firstName: '',
        lastName: '',
        birthDate: '',
        graduationDate: '',
        schoolId: '',
        schoolCode: '',
        schoolName: '',
        socialSecurityLast4: null,
        subServices: [],
        validatedStatus: 2,
        phone: null,
        personalAddress: null,
        email: null,
        informationCertified: false,
        credentialReleaseConsent: false,
        licenseNumber: null,
        issuingState: null,
        programPartnerId: null
      });
      // Transportation is UI-only until backend endpoints support it.
      if (type === ProviderType.Transportation) {
        this.subServices.set([]);
        this.createForm();
        return;
      }
      this.getEmployeeSubServices(this.employeeData());
    }
  }

  public onNewEmployeeClick(): void {
    this.breadcrumbItems.set([
      { label: EMPLOYEES_BREADCRUMB_LABEL, route: EMPLOYEES_LIST_ROUTE },
      { label: ADD_NEW_EMPLOYEE_LABEL(this.getOccupation()) }
    ]);
    this.selectedId = null;
    this.formCreated = false;
    this.employeeData.set({} as EmployeeDataModel);
    this.consentFormResult.set({
      informationConsent: false,
      credentialReleaseConsent: false
    });
    this.resetForm();
  }

  public resetForm(): void {
    this.selectingType.set(true);
    this.resetEmployeeForm();
  }

  public getISODate(date: string): string {
    //return ISO date instead of MM/DD/YYYY
    return new Date(date).toISOString();
  }

  public getFieldErrorId(fieldName: string, index = 0): string {
    return `employee-${fieldName}-${index}-error`;
  }

  private getSubmitDate(date: string | Date): string {
    return new Date(date).toISOString().split('T')[0];
  }

  public isFieldEditable(fieldName: string): boolean {
    if (this.viewOnly) {
      return false;
    }
    if (!this.editableFieldsAllowlist) {
      return true;
    }
    return this.editableFieldsAllowlist.has(fieldName);
  }

  private parseEditableFieldsAllowlist(raw: string | null): void {
    if (!raw?.trim()) {
      this.editableFieldsAllowlist = null;
      return;
    }
    this.editableFieldsAllowlist = new Set(
      raw
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean)
    );
  }

  private requiredSubServicesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (Array.isArray(value)) {
        return value.length > 0 ? null : { required: true };
      }
      return value ? null : { required: true };
    };
  }

  private buildTherapySubmitPayload(): EmployeeSubmitPayload {
    const detail = this.details.at(0) as FormGroup;
    const subServices = ((detail.controls['subServices'].value ?? []) as SubService[]);

    return {
      ...(this.selectedId ? { id: this.employeeData().id } : {}),
      firstName: detail.controls['firstName'].value,
      lastName: detail.controls['lastName'].value,
      employeeType: 2,
      birthDate: this.getSubmitDate(detail.controls['birthDate'].value),
      validatedStatus: 2,
      licenseNumber: detail.controls['licenseNumber'].value.toString(),
      issuingState: detail.controls['stateCtrl'].value.code,
      npiNumber: detail.controls['npiNumber'].value?.toString() ?? null,
      npiNumber2: detail.controls['npiNumber2'].value?.toString() ?? null,
      socialSecurityLast4: detail.controls['socialSecurityLast4'].value,
      email: detail.controls['email'].value,
      programPartnerId: this.providerId ?? '',
      personalAddress: {
        ...(this.selectedId ? { id: this.employeeData().personalAddress?.id } : {}),
        address1: detail.controls['address'].get('address1')?.value,
        address2: null,
        city: detail.controls['address'].get('city')?.value,
        state: detail.controls['address'].get('state')?.value?.code,
        zip: detail.controls['address'].get('zipcode')?.value,
        county: detail.controls['address'].get('county')?.value
      },
      subServices: subServices.map((service) => ({
        id: service.id,
        name: service.name,
        description: service.description ?? ''
      }))
    };
  }

  public buildTutorSubmitPayload(): EmployeeSubmitPayload {
    const detail = this.details.at(0) as FormGroup;
    const school = detail.controls['schoolCtrl'].value;
    const subServices = ((detail.controls['subServices'].value ?? []) as SubService[]);

    return {
      ...(this.selectedId ? { id: this.employeeData().id } : {}),
      firstName: detail.controls['firstName'].value,
      lastName: detail.controls['lastName'].value,
      employeeType: 1,
      birthDate: this.getSubmitDate(detail.controls['birthDate'].value),
      validatedStatus: 2,
      graduationDate: this.getSubmitDate(detail.controls['graduationDate'].value),
      schoolId: school.id,
      schoolCode: school.schoolCode,
      schoolName: school.schoolName,
      socialSecurityLast4: detail.controls['socialSecurityLast4'].value,
      informationCertified: this.consentFormResult()?.informationConsent ?? false,
      credentialReleaseConsent: this.consentFormResult()?.credentialReleaseConsent ?? false,
      programPartnerId: this.providerId ?? '',
      subServices: subServices.map((service) => ({
        id: service.id,
        name: service.name,
        description: service.description ?? ''
      })),
      phone: '',
      email: detail.controls['email'].value,
      personalAddress: {
        ...(this.selectedId ? { id: this.employeeData().personalAddress?.id } : {}),
        address1: detail.controls['address'].get('address1')?.value,
        address2: null,
        city: detail.controls['address'].get('city')?.value,
        state: detail.controls['address'].get('state')?.value?.code,
        zip: detail.controls['address'].get('zipcode')?.value,
        county: detail.controls['address'].get('county')?.value
      },
    };
  }

  public onSubmit(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();
      this.employeeForm.updateValueAndValidity({ emitEvent: true });
      this.cdr.markForCheck();
      return;
    }

    this.loadingSpinnerService.showSpinner();

    if (this.employeeData().employeeType === ProviderType.Tutor) {
      this.consentcheck();
      return;
    }

    if (this.employeeData().employeeType === ProviderType.Therapy) {
      this.addUpdateEmployee(this.buildTherapySubmitPayload());
      return;
    }

    // Transportation create/update is not hooked to APIs yet.
    this.loadingSpinnerService.hideSpinner();
  }

  public onCancelClick(): void {
    this.router.navigate([EMPLOYEES_LIST_ROUTE]);
  }

  public onBackClick(): void {
    this.breadcrumbItems.set([
      { label: EMPLOYEES_BREADCRUMB_LABEL, route: EMPLOYEES_LIST_ROUTE },
      { label: ADD_NEW_EMPLOYEE_LABEL(this.getOccupation()) }
    ]);
    this.formCreated = false;
    this.employeeData.set({} as EmployeeDataModel);
    this.resetForm();
    this.cdr.markForCheck();
  }

  public onDeleteClick(employee: EmployeeDataModel): void {
    const employeeName = employee.firstName + ' ' + employee.lastName;
    this.dialogRef = this.dialogService.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        align: 'center',
        checkMark: false,
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        message: `Are you sure that you want to delete ${employeeName}?`,
        modalType: ModalType.ConfirmModal,
        title: 'Delete Employee'
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    this.dialogRef.componentInstance.onConfirmModal
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe(() => {
        this.dialogRef.close();
        this.deleteEmployee(employee.id as string);
      });
  }

  private consentcheck(): void {
    this.dialogService
      .open(ModalDialogComponent, {
        backdropClass: 'custom-dialog-backdrop',
        panelClass: 'consent-dialog',
        disableClose: true,
        data: {
          modalType: ModalType.CustomModal,
          component: ConsentFormComponent,
          consentFormData: { ...this.consentFormResult(), isEditMode: !!this.selectedId }
        },
        width: '45vw',
        maxWidth: '90vw'
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe((result: ConsentFormResult | undefined) => {
        if (!result) {
          this.loadingSpinnerService.hideSpinner();
          return;
        }
        this.consentFormResult.set(result);
        const payload = this.buildTutorSubmitPayload();
        this.addUpdateEmployee(payload);
      });
  }

  private addUpdateEmployee(payload: EmployeeSubmitPayload): void {
    if (!payload) {
      return;
    }

    if (this.selectedId) {
      // update
      this.employeeService
        .UpdateEmployee(payload)
        .pipe(take(1), takeUntil(this.onDestroy))
        .subscribe({
          next: () => {
            this.loadingSpinnerService.hideSpinner();
            this.toastMessageService.showToast(ToastMessageType.Success, `Employee updated successfully !`);
            this.router.navigate([EMPLOYEES_LIST_ROUTE]);
          },
          error: (error: { error?: { message?: string } }) => {
            this.loadingSpinnerService.hideSpinner();
            const errorMessage = error?.error?.message || 'Failed to update employee.';
            this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
            this.toastService.showToast(ToastMessageType.Error, 'Failed to update employee. Please try again later.');
          }
        });
    } else {
      // new
      delete payload.id;
      this.employeeService
        .CreateEmployee(payload)
        .pipe(take(1), takeUntil(this.onDestroy))
        .subscribe({
          next: () => {
            this.loadingSpinnerService.hideSpinner();
            this.toastMessageService.showToast(ToastMessageType.Success, `Employee created successfully !`);
            this.router.navigate([EMPLOYEES_LIST_ROUTE]);
          },
          error: (error: { error?: { message?: string } }) => {
            this.loadingSpinnerService.hideSpinner();
            const errorMessage = error?.error?.message || 'Failed to create employee.';
            this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
            this.toastService.showToast(ToastMessageType.Error, 'Failed to create employee. Please try again later.');
          }
        });
    }
  }

  private deleteEmployee(employeeId: string): void {
    this.loadingSpinnerService.showSpinner();
    this.providerService.deleteEmployee(employeeId).subscribe({
      next: () => {
        this.loadingSpinnerService.hideSpinner();
        this.router.navigate([EMPLOYEES_LIST_ROUTE]);
        this.toastService.showToast(ToastMessageType.Success, 'Employee deleted successfully.');
      },
      error: (error: { error?: { message?: string } }) => {
        this.loadingSpinnerService.hideSpinner();
        const errorMessage = error?.error?.message || 'Failed to delete employee.';
        this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
        this.toastService.showToast(ToastMessageType.Error, 'Failed to delete employee. Please try again later.');
      }
    });
  }

  private getDataForNew(): void {
    this.loadingSpinnerService.showSpinner();
    forkJoin({
      schoolsRes: this.employeeService.getSchools().pipe(take(1), takeUntil(this.onDestroy)),
      statesRes: this.employeeService.getStates().pipe(take(1), takeUntil(this.onDestroy))
    }).subscribe({
      next: ({ schoolsRes, statesRes }) => {
        this.schools = schoolsRes;
        this.states = statesRes;
        this.loadingSpinnerService.hideSpinner();
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: { error?: { message?: string } }) => {
        this.loading.set(false);
        this.loadingSpinnerService.hideSpinner();
        const errorMessage = error?.error?.message || FAILED_TO_LOAD_DATA;
        this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
        this.toastService.showToast(ToastMessageType.Error, FAILED_TO_LOAD_DATA_TOAST);
        this.cdr.markForCheck();
      }
    });
  }

  private getData(id: string): void {
    this.loading.set(true);
    this.resetEmployeeForm();
    forkJoin({
      employeeRes: this.employeeService.GetEmployeeDetails(id).pipe(take(1), takeUntil(this.onDestroy)),
      schoolsRes: this.employeeService.getSchools().pipe(take(1), takeUntil(this.onDestroy)),
      statesRes: this.employeeService.getStates().pipe(take(1), takeUntil(this.onDestroy))
    }).subscribe({
      next: ({ employeeRes, schoolsRes, statesRes }) => {
        this.schools = schoolsRes;
        this.states = statesRes;
        this.getEmployeeSubServices(employeeRes);
      },
      error: (error: { error?: { message?: string } }) => {
        this.loading.set(false);
        this.loadingSpinnerService.hideSpinner();
        const errorMessage = error?.error?.message || FAILED_TO_LOAD_DATA;
        this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
        this.toastService.showToast(ToastMessageType.Error, FAILED_TO_LOAD_DATA_TOAST);
        this.cdr.markForCheck();
      }
    });
  }

  private getEmployeeSubServices(res: EmployeeDataModel): void {
    this.loading.set(true);
    this.loadingSpinnerService.showSpinner();
    const type = String(res.employeeType ?? '').trim().toLowerCase();
    const employeeType: 1 | 2 = type === '1' || type === 'tutor' || type === 'tutoring' ? 1 : 2;
    this.employeeService
      .GetEmployeeSubServices(employeeType)
      .pipe(take(1), takeUntil(this.onDestroy))
      .subscribe({
        next: (subServiceRes) => {
          this.subServices.set(subServiceRes.map((service) => ({
            ...service,
            description: service.description ?? ''
          })));
          res.employeeType = employeeType === 1 ? ProviderType.Tutor : ProviderType.Therapy;
          const socialSecurityLast4 = res.socialSecurityLast4;
          if (socialSecurityLast4 && socialSecurityLast4.trim().length > 4) {
            res.socialSecurityLast4 = socialSecurityLast4.slice(-4);
          }
          if (this.selectedId) {
            this.breadcrumbItems.set([
              { label: EMPLOYEES_BREADCRUMB_LABEL, route: EMPLOYEES_LIST_ROUTE },
              { label: `Edit ${res.firstName} ${res.lastName}`.trim() }
            ]);
          }
          this.employeeData.set(res);
          if (!this.formCreated) {
            this.createForm();
          } else {
            this.loading.set(false);
          }
          this.loadingSpinnerService.hideSpinner();
          this.cdr.markForCheck();
        }, error: (error: { error?: { message?: string } }) => {
          this.loading.set(false);
          this.loadingSpinnerService.hideSpinner();
          const errorMessage = error?.error?.message || FAILED_TO_LOAD_DATA;
          this.appInsightsService.log(new Error(errorMessage), SeverityLevel.error);
          this.toastService.showToast(ToastMessageType.Error, FAILED_TO_LOAD_DATA_TOAST);
          this.cdr.markForCheck();
        }
      });
  }

  private createForm(): void {
    this.loading.set(true);
    let detailGroup: FormGroup;
    const employeeData = this.employeeData();
    switch (employeeData.employeeType) {
      case ProviderType.Tutor:
        this.consentFormResult.set({
          informationConsent: employeeData.informationCertified ?? false,
          credentialReleaseConsent: employeeData.credentialReleaseConsent ?? false
        });
        detailGroup = this.createTutorDetailForm(employeeData);
        detailGroup.controls['schoolCtrl'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
          this.filterSchools(typeof value === 'string' ? value : '');
        });
        break;
      case ProviderType.Therapy:
        detailGroup = this.createTherapyDetailForm(employeeData);
        detailGroup.controls['stateCtrl'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((value) => {
          this.filterStates(typeof value === 'string' ? value : '');
        });
        break;
      case ProviderType.Transportation:
        detailGroup = this.createTransportationDetailForm(employeeData);
        break;
      default:
        detailGroup = this.fb.group({});
        break;
    }

    this.setupAddressStateValueChanges(detailGroup);
    this.details.push(detailGroup);

    this.filteredSchools.next(this.schools.slice());
    this.filteredStates.next(this.states.slice());
    this.loading.set(false);
    this.formCreated = true;
    this.cdr.markForCheck();
  }

  private createTutorDetailForm(employeeData: EmployeeDataModel): FormGroup {
    const formData = employeeData as EmployeeDetailFormData;
    const selectedSchool = this.selectedId ? this.schools.find((x) => x.id == employeeData.schoolId) : null;

    const selectedServices = this.selectedId
      ? (this.subServices()?.filter((x) => (employeeData.subServices as SubService[])?.find((y) => y.id == x.id)) ?? [])
      : [];

    return this.fb.group({
      firstName: [
        { value: this.selectedId ? employeeData.firstName : '', disabled: !this.isFieldEditable('firstName') },
        [Validators.required, nameValidator()]
      ],
      lastName: [
        { value: this.selectedId ? employeeData.lastName : '', disabled: !this.isFieldEditable('lastName') },
        [Validators.required, nameValidator()]
      ],
      socialSecurityLast4: [
        {
          value: this.selectedId ? employeeData.socialSecurityLast4?.trim() : '',
          disabled: !this.isFieldEditable('socialSecurityLast4')
        },
        [Validators.required, ssnLast4Validator()]
      ],
      email: [
        { value: this.selectedId ? formData.email ?? '' : '', disabled: !this.isFieldEditable('email') },
        [Validators.required, requiredEmailValidator()]
      ],
      schoolCtrl: [
        { value: this.selectedId ? selectedSchool : '', disabled: !this.isFieldEditable('schoolCtrl') },
        [Validators.required]
      ],
      subServices: [
        { value: this.selectedId ? selectedServices : [], disabled: !this.isFieldEditable('subServices') },
        [Validators.required, this.requiredSubServicesValidator()]
      ],
      birthDate: [
        { value: this.selectedId ? employeeData.birthDate : '', disabled: !this.isFieldEditable('birthDate') },
        [Validators.required, pastDateValidator(), minYearValidator(MIN_REALISTIC_YEAR)]
      ],
      graduationDate: [
        {
          value: this.selectedId ? employeeData.graduationDate : '',
          disabled: !this.isFieldEditable('graduationDate')
        },
        [Validators.required, pastDateValidator(), minYearValidator(MIN_REALISTIC_YEAR)]
      ],
      address: this.addAddressForm(employeeData?.personalAddress)
    });
  }

  private createTherapyDetailForm(employeeData: EmployeeDataModel): FormGroup {
    const formData = employeeData as EmployeeDetailFormData;
    const issuingState = this.selectedId ? this.states.find((x) => x.code == employeeData.issuingState) : '';

    const selectedServices = this.selectedId
      ? (this.subServices()?.filter((x) => (employeeData.subServices as SubService[])?.find((y) => y.id == x.id)) ?? [])
      : [];

    return this.fb.group({
      firstName: [
        { value: this.selectedId ? employeeData.firstName : '', disabled: !this.isFieldEditable('firstName') },
        [Validators.required, nameValidator()]
      ],
      lastName: [
        { value: this.selectedId ? employeeData.lastName : '', disabled: !this.isFieldEditable('lastName') },
        [Validators.required, nameValidator()]
      ],
      socialSecurityLast4: [
        {
          value: this.selectedId ? employeeData.socialSecurityLast4?.trim() : '',
          disabled: !this.isFieldEditable('socialSecurityLast4')
        },
        [Validators.required, ssnLast4Validator()]
      ],
      licenseNumber: [
        {
          value: this.selectedId ? employeeData.licenseNumber : null,
          disabled: !this.isFieldEditable('licenseNumber')
        },
        [Validators.required, Validators.min(0)]
      ],
      email: [
        { value: this.selectedId ? formData.email ?? '' : '', disabled: !this.isFieldEditable('email') },
        [Validators.required, requiredEmailValidator()]
      ],
      stateCtrl: [
        { value: this.selectedId ? issuingState : '', disabled: !this.isFieldEditable('stateCtrl') },
        [Validators.required]
      ],
      birthDate: [
        { value: this.selectedId ? employeeData.birthDate : '', disabled: !this.isFieldEditable('birthDate') },
        [Validators.required, pastDateValidator(), minYearValidator(MIN_REALISTIC_YEAR)]
      ],
      subServices: [
        { value: this.selectedId ? selectedServices : [], disabled: !this.isFieldEditable('subServices') },
        [Validators.required, this.requiredSubServicesValidator()]
      ],
      npiNumber: [
        { value: this.selectedId ? formData.npiNumber ?? '' : '', disabled: !this.isFieldEditable('npiNumber') },
        [tenDigitNumberValidator()]
      ],
      npiNumber2: [
        { value: this.selectedId ? formData.npiNumber2 ?? '' : '', disabled: !this.isFieldEditable('npiNumber2') },
        [tenDigitNumberValidator()]
      ],
      address: this.addAddressForm(employeeData?.personalAddress)
    });
  }

  /** Cosmetic Transportation onboarding form — no create/update payload wiring yet. */
  private createTransportationDetailForm(employeeData: EmployeeDataModel): FormGroup {
    const formData = employeeData as EmployeeDetailFormData;

    return this.fb.group({
      firstName: [
        { value: this.selectedId ? employeeData.firstName : '', disabled: !this.isFieldEditable('firstName') },
        [Validators.required, nameValidator()]
      ],
      lastName: [
        { value: this.selectedId ? employeeData.lastName : '', disabled: !this.isFieldEditable('lastName') },
        [Validators.required, nameValidator()]
      ],
      socialSecurityLast4: [
        {
          value: this.selectedId ? employeeData.socialSecurityLast4?.trim() : '',
          disabled: !this.isFieldEditable('socialSecurityLast4')
        },
        [Validators.required, ssnLast4Validator()]
      ],
      email: [
        { value: this.selectedId ? formData.email ?? '' : '', disabled: !this.isFieldEditable('email') },
        [Validators.required, requiredEmailValidator()]
      ],
      birthDate: [
        { value: this.selectedId ? employeeData.birthDate : '', disabled: !this.isFieldEditable('birthDate') },
        [Validators.required, pastDateValidator(), minYearValidator(MIN_REALISTIC_YEAR)]
      ],
      address: this.addAddressForm(employeeData?.personalAddress)
    });
  }

  private resetEmployeeForm(): void {
    this.employeeForm = this.fb.group({
      details: this.fb.array([])
    });
  }

  private filterSchools(search: string): void {
    if (!this.schools) {
      return;
    }
    const searchTerm = (search ?? '').toLowerCase();
    if (!searchTerm) {
      this.filteredSchools.next(this.schools.slice());
      return;
    }

    this.filteredSchools.next(
      this.schools.filter(
        (school) =>
          school.schoolName.toLowerCase().includes(searchTerm) || school.schoolCode.toLowerCase().includes(searchTerm)
      )
    );
  }

  private filterStates(search: string): void {
    if (!this.states) {
      return;
    }
    const searchTerm = (search ?? '').toLowerCase();
    if (!searchTerm) {
      this.filteredStates.next(this.states.slice());
      return;
    }

    this.filteredStates.next(this.states.filter((state) => state.name.toLowerCase().includes(searchTerm)));
  }

  private setupAddressStateValueChanges(detailGroup: FormGroup): void {
    const stateControl = detailGroup.get('address.state');
    const countyControl = detailGroup.get('address.county');

    if (!stateControl || !countyControl) {
      return;
    }

    stateControl.valueChanges
      .pipe(startWith(stateControl.value), takeUntil(this.onDestroy))
      .subscribe((stateValue: string | State | null | undefined) => {
        const stateCode = this.resolveStateCode(stateValue);
        const isNorthCarolina = stateCode === COUNTY_REQUIRED_STATE_CODE;

        this.isNorthCarolina.set(isNorthCarolina);
        this.updateCountyValidator(countyControl, isNorthCarolina);

        if (isNorthCarolina) {
          this.loadCounties(COUNTY_REQUIRED_STATE_CODE, detailGroup);
          return;
        }

        this.counties = [];
        this.filteredCounties = of([]);
        countyControl.patchValue('', { emitEvent: false });
      });
  }

  private resolveStateCode(stateValue: string | State | null | undefined): string | undefined {
    if (typeof stateValue === 'object' && stateValue?.code) {
      return stateValue.code;
    }

    if (typeof stateValue === 'string') {
      const normalizedValue = stateValue.trim().toLowerCase();
      const matchedState = this.states.find(
        (state) => state.code.toLowerCase() === normalizedValue || state.name.toLowerCase() === normalizedValue
      );
      return matchedState?.code;
    }

    return undefined;
  }

  /**
   *  Resolves the state selection for the address form based on the provided state value. 
   * It checks if the state value is an object or a string and attempts to find a matching state from the list of available states. 
   * If a match is found, it returns the corresponding State object; otherwise, it returns an empty string.
   * @param stateValue 
   * @returns The resolved State object if a match is found, 
   * or an empty string if no match is found or if the state value is null/undefined.
   */
  private resolveAddressStateSelection(stateValue: Address['state']): State | '' {
    if (!stateValue) {
      return '';
    }

    if (typeof stateValue === 'object') {
      const inputName = stateValue.name?.trim().toLowerCase();
      const inputCode =
        'code' in stateValue && typeof stateValue.code === 'string' ? stateValue.code.trim().toLowerCase() : '';

      return (
        this.states.find(
          (state) =>
            (!!inputCode && state.code.toLowerCase() === inputCode) ||
            (!!inputName && state.name.toLowerCase() === inputName)
        ) ?? ''
      );
    }

    const normalizedValue = stateValue.trim().toLowerCase();
    return (
      this.states.find(
        (state) => state.code.toLowerCase() === normalizedValue || state.name.toLowerCase() === normalizedValue
      ) ?? ''
    );
  }

  private updateFormDisabledState(): void {
    if (this.employeeForm && this.details.length > 0 && this.formCreated) {
      // Recreate the form with the new disabled state
      this.resetEmployeeForm();
      this.createForm();
    }
  }

  private getOccupation(type?: ProviderType): string {
    switch (type) {
      case ProviderType.Tutor:
        return 'Tutor';
      case ProviderType.Therapy:
        return 'Therapist';
      case ProviderType.Transportation:
        return 'Transportation';
      default:
        return 'Employee';
    }
  }

  private loadCounties(state: string, detailGroup: FormGroup): void {
    const countyControl = detailGroup.get('address.county');
    if (!countyControl) {
      this.filteredCounties = of([]);
      return;
    }

    this.countiesService.getCountiesByState(state).subscribe({
      next: (data: CountyResponse[]) => {
        this.counties = data;
        countyControl.updateValueAndValidity({ emitEvent: true });
        this.filteredCounties = countyControl.valueChanges.pipe(
          startWith((countyControl.value as string | null) ?? ''),
          map((value: string | null) => this._filterCounties(value ?? ''))
        );
      },
      error: () => {
        this.counties = [];
        this.filteredCounties = of([]);
      }
    });
  }

  private updateCountyValidator(countyControl: AbstractControl, isRequired: boolean): void {
    if (isRequired) {
      countyControl.setValidators([Validators.required]);
    } else {
      countyControl.clearValidators();
    }
    countyControl.updateValueAndValidity({ emitEvent: false });
  }

  private _filterCounties(value: string): CountyResponse[] {
    const filterValue = value.toLowerCase();
    return this.counties.filter((county) => county.name.toLowerCase().includes(filterValue));
  }

  private addAddressForm(addressData?: Address | null): FormGroup {
    const resolvedAddressState = this.selectedId ? this.resolveAddressStateSelection(addressData?.state) : '';

    return this.fb.group({
      address1: [
        { value: this.selectedId ? addressData?.address1 : '', disabled: !this.isFieldEditable('address1') }, 
        [Validators.required, noWhitespaceValidator()]
      ],
      city: [
        { value: this.selectedId ? addressData?.city : '', disabled: !this.isFieldEditable('city') },
        [Validators.required, nameValidator()]
      ],
      state: [
        { value: resolvedAddressState, disabled: !this.isFieldEditable('state') },
        Validators.required
      ],
      zipcode: [
        { value: this.selectedId ? addressData?.zip : '', disabled: !this.isFieldEditable('zipcode') },
        [Validators.required, zipCodeValidator()]
      ],
      county: [
        { value: this.selectedId ? addressData?.county : '', disabled: !this.isFieldEditable('county') }
      ]
    });
  }
}
